// **The world rectangle a staged chapter needs on screen** — extracted from `DemoStage.tsx`
// (presentation-polish Wave 2, 2026-08-11) and re-exported from there, so every existing importer
// is unchanged.
//
// It moved because it grew a **second** consumer with a very different shape. `stageFrameOf` is a
// pure function of a `DemoStageSpec` and always was, but it lived beside the camera that consumes
// it, in a React component module that pulls in hooks, gesture handlers and the canvas adapter.
// `chapters.ts` now needs the same rectangle to place the raid's entry point *outside* the frame
// (see `ENEMY_RAID_POSITION`), and a plain content module importing a component module to get one
// number is the wrong dependency direction — the alternative, recomputing the frame's arithmetic in
// `chapters.ts`, is worse: two statements of the same rectangle, free to drift, with the drift
// showing up on stage as a raider that fades in mid-shot instead of flying in.

import type { DemoStageSpec } from './stage-driver';

/** The world rectangle a chapter's staged content occupies — centre plus half-extents, in world
 * units. Produced by {@link stageFrameOf}, consumed by `defaultStageCamera`. */
export interface StageFrame {
  readonly centre: readonly [number, number];
  readonly halfX: number;
  readonly halfY: number;
}

/** A patch's own world radius when a stage does not name one. Shared with `DemoStage.tsx`'s
 * snapshot adapter, which defaults a patch the same way. */
export const DEFAULT_PATCH_RADIUS_U = 15;

/** Breathing room around the staged content, on top of a patch's own radius: the frame's edge is
 * where the camera stops, and a flower flush against it reads as clipped. */
const STAGE_FRAME_MARGIN_U = 40;

/** The smallest half-extent the frame ever reports. A patchless (or single-point) stage would
 * otherwise divide by a zero half-extent and project an infinite camera — and a hive alone still
 * wants its surrounds, not a scale that fills the projector with one comb. */
const STAGE_FRAME_MIN_HALF_U = 120;

/**
 * **The world rectangle a staged chapter needs on screen** (playthrough finding, 2026-08-09).
 *
 * The presenter colony's hive plus **all** the patches — deliberately **not** the other colonies'
 * hives. The enemy colony sits far off to one side and stages no bee at all (`chapters.ts`), so
 * including it would halve the zoom for a hive the audience never looks at, to keep an empty corner
 * of the field in shot.
 */
export function stageFrameOf(stage: DemoStageSpec): StageFrame {
  // Colony 0 is the presenter's own in every staged chapter — the convention `DemoApp`,
  // `TypeEditor` and `BeekeeperPromptBar` all fix.
  const hive = stage.colonies[0]?.hive;
  const points: readonly (readonly [number, number])[] = [
    ...(hive === undefined ? [] : [[hive.x, hive.y] as const]),
    ...stage.patches.map((patch) => [patch.position.x, patch.position.y] as const),
  ];
  if (points.length === 0) {
    return { centre: [0, 0], halfX: STAGE_FRAME_MIN_HALF_U, halfY: STAGE_FRAME_MIN_HALF_U };
  }
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const [minX, maxX] = [Math.min(...xs), Math.max(...xs)];
  const [minY, maxY] = [Math.min(...ys), Math.max(...ys)];
  const pad = DEFAULT_PATCH_RADIUS_U + STAGE_FRAME_MARGIN_U;
  return {
    centre: [(minX + maxX) / 2, (minY + maxY) / 2],
    halfX: Math.max(STAGE_FRAME_MIN_HALF_U, (maxX - minX) / 2 + pad),
    halfY: Math.max(STAGE_FRAME_MIN_HALF_U, (maxY - minY) / 2 + pad),
  };
}
